import { MAP_UI } from '../theme/mapViewUi';

/** Match keys for correlating neighbor AP_NodeID entries to a device. */
export function deviceAssociationMatchKeys(device) {
  const keys = new Set();
  (device?.radios || []).forEach((radio) => {
    const mac = radio.mac?.toLowerCase()?.replace(/:/g, '');
    if (mac) keys.add(mac);
  });
  const serial = device?.serial_number?.toLowerCase();
  if (serial) {
    keys.add(serial);
    keys.add(serial.replace(/:/g, ''));
  }
  return keys;
}

export function normalizeNeighborApNodeId(raw) {
  return String(raw || '').trim().toLowerCase().replace(/:/g, '');
}

export function neighborEntryRssi(neighbor) {
  const apSNR = neighbor?.AP_SNR ?? neighbor?.ap_snr ?? 0;
  const staSNR = neighbor?.STA_SNR ?? neighbor?.sta_snr ?? 0;
  const apNF = neighbor?.AP_NF ?? neighbor?.ap_nf ?? 0;
  const staNF = neighbor?.STA_NF ?? neighbor?.sta_nf ?? 0;
  return {
    apRssi: apSNR + apNF,
    staRssi: staSNR + staNF,
    channel: neighbor?.Channel ?? neighbor?.channel ?? 0,
  };
}

export function rssiSignalColor(rssi) {
  if (rssi >= -50) return MAP_UI.deviceOnlineBorder;
  if (rssi >= -70) return MAP_UI.moderate;
  return MAP_UI.deviceOfflineBorder;
}

/**
 * Find every device whose neighbor scan heard `targetDevice` at or above
 * `minStaRssiDbm` on the STA direction (scanner → target).
 */
export function findDevicesThatHeardTarget(allReports, targetDevice, minStaRssiDbm, devicesBySerial) {
  if (!targetDevice?.serial_number) {
    return { links: [], hearerSerials: new Set() };
  }

  const targetKeys = new Set(deviceAssociationMatchKeys(targetDevice));
  const targetSerial = targetDevice.serial_number.toLowerCase();
  const links = [];
  const hearerSerials = new Set();
  const bestByScanner = new Map();

  (allReports || []).forEach((report) => {
    const scannerSerial = String(report?.Serial || report?.serial || '').trim().toLowerCase();
    if (!scannerSerial || scannerSerial === targetSerial) return;

    const neighbors = report?.Neighbors || report?.neighbors || [];
    neighbors.forEach((neighbor, index) => {
      const apNodeId = normalizeNeighborApNodeId(neighbor?.AP_NodeID || neighbor?.ap_node_id);
      if (!apNodeId || !targetKeys.has(apNodeId)) return;

      const { apRssi, staRssi, channel } = neighborEntryRssi(neighbor);
      if (!Number.isFinite(staRssi) || staRssi < minStaRssiDbm) return;

      const scannerDevice = devicesBySerial.get(scannerSerial);
      if (!scannerDevice?.lat || !scannerDevice?.lng) return;

      const link = {
        id: `${scannerSerial}-${apNodeId}-${index}`,
        scannerSerial,
        scannerDevice,
        staRssi,
        apRssi,
        channel,
      };

      const prev = bestByScanner.get(scannerSerial);
      if (!prev || staRssi > prev.staRssi) {
        bestByScanner.set(scannerSerial, link);
      }
    });
  });

  bestByScanner.forEach((link) => {
    links.push(link);
    hearerSerials.add(link.scannerSerial);
  });

  links.sort((a, b) => b.staRssi - a.staRssi);
  return { links, hearerSerials };
}

/**
 * Build map overlay links from GET /tree-mgmt/:serial/heard-by response.
 */
export function buildHeardByLinksFromResponse(hearers, devicesBySerial) {
  const links = [];
  const hearerSerials = new Set();
  (hearers || []).forEach((h, index) => {
    const scannerSerial = String(h.scanner_serial || h.scannerSerial || '')
      .trim()
      .toLowerCase();
    if (!scannerSerial) return;
    const scannerDevice = devicesBySerial.get(scannerSerial);
    if (!scannerDevice?.lat || !scannerDevice?.lng) return;
    const staRssi = h.sta_rssi ?? h.staRssi;
    const apRssi = h.ap_rssi ?? h.apRssi;
    const channel = h.channel ?? 0;
    links.push({
      id: `${scannerSerial}-heard-by-${index}`,
      scannerSerial,
      scannerDevice,
      staRssi,
      apRssi,
      channel,
    });
    hearerSerials.add(scannerSerial);
  });
  links.sort((a, b) => b.staRssi - a.staRssi);
  return { links, hearerSerials };
}

/** Resolve CAP devices for a heard-by device set (explicit CAPs + cap_serial refs). */
export function resolveCapDevicesForHeardSet(deviceList, allDevices, isCapDevice) {
  const capSerials = new Set();
  (deviceList || []).forEach((device) => {
    if (!device) return;
    if (isCapDevice(device)) {
      capSerials.add(String(device.serial_number).toLowerCase());
      return;
    }
    const cap = String(device.cap_serial || '').trim().toLowerCase();
    if (cap) capSerials.add(cap);
  });

  return (allDevices || []).filter((d) => (
    d?.serial_number && capSerials.has(String(d.serial_number).toLowerCase())
  ));
}

export function nodeDevicesFromHeardSet(deviceList, isCapDevice) {
  return (deviceList || []).filter((d) => d && !isCapDevice(d));
}
